import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Building2, Mail, Plane, User, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_contacts: 0,
    total_asociaciones: 0,
    total_empresas: 0,
    total_particulares: 0,
    total_agencias_viajes: 0,
    total_colegios: 0,
    total_campaigns: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Contactos',
      value: stats.total_contacts,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Empresas',
      value: stats.total_empresas,
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Asociaciones',
      value: stats.total_asociaciones,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Agencias de Viajes',
      value: stats.total_agencias_viajes,
      icon: Plane,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Colegios',
      value: stats.total_colegios,
      icon: GraduationCap,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
    {
      title: 'Particulares',
      value: stats.total_particulares,
      icon: User,
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
    },
    {
      title: 'Campañas',
      value: stats.total_campaigns,
      icon: Mail,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Gestiona tus contactos y campañas de email para tus hoteles
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.bgColor} p-2 rounded-lg`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {loading ? '...' : stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Bienvenido a HotelMail Pro</CardTitle>
            <CardDescription>
              Gestiona el email marketing de tus 3 hoteles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">🏨 Tus Hoteles:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Hotel Ciutat Martorell</li>
                <li>Port Sitges Hotel</li>
                <li>Hostal San Francisco</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">✨ Funcionalidades:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Gestión de 2000+ contactos</li>
                <li>Segmentación: Asociaciones, Empresas, Agencias de Viajes, Colegios, Particulares</li>
                <li>Plantillas de email personalizadas</li>
                <li>Envío masivo automatizado</li>
                <li>Landing page de hoteles</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inicio Rápido</CardTitle>
            <CardDescription>
              Pasos para empezar a enviar campañas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium text-gray-900">Importa tus contactos</p>
                  <p className="text-sm text-gray-600">Sube un archivo CSV con tus 2000 contactos</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium text-gray-900">Crea plantillas</p>
                  <p className="text-sm text-gray-600">Diferentes mensajes para asociaciones y empresas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium text-gray-900">Lanza campañas</p>
                  <p className="text-sm text-gray-600">Envía emails masivos segmentados</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  4
                </div>
                <div>
                  <p className="font-medium text-gray-900">Comparte tu landing</p>
                  <p className="text-sm text-gray-600">Muestra tus hoteles y salas de reuniones</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;